import type { TranslateService } from '@ngx-translate/core';

import { findActivePaymentMethod, type PaymentMethod } from '@integrations/payment-methods';
import type { PaymentElementLegacy } from '@sites/entity-core';
import { createPaymentElementLegacy } from '@sites/entity-core';
import type { PaymentElementFormGroup } from '@common/types/payment-element.form';
import { EPaymentType } from '@common/enums';
import { getPaymentMethodCurrency } from '@common/helpers/payment';
import { paymentElementFormGroup } from '@web/widgets/common/base/base-form/base-form.form';

export { findActivePaymentMethod };

export const getDefaultPaymentElementData = (
    paymentMethod: PaymentMethod | null
): PaymentElementFormGroup['value'] => ({
    type: paymentMethod?.type ?? EPaymentType.custom,
    payment_method_id: paymentMethod?.id ?? 'custom_payment_id',
    currency: getPaymentMethodCurrency(paymentMethod?.settings) || 'USD',
    isEditableTotalCost: false,
    isActive: true,
});

export const createDefaultPaymentElementForm = (
    paymentMethods: PaymentMethod[],
    translateService: TranslateService
): PaymentElementFormGroup => {
    const paymentMethod = findActivePaymentMethod(paymentMethods);
    const form = paymentElementFormGroup(translateService);
    form.patchValue(getDefaultPaymentElementData(paymentMethod));
    return form;
};

/**
 * Signal-form twin of {@link getDefaultPaymentElementData}: only the fields derived
 * from the picked payment method, so callers can merge it *over* an existing element
 * without clobbering a configured amount / custom name.
 */
export const getDefaultPaymentElementLegacyData = (
    paymentMethod: PaymentMethod | null
): Partial<PaymentElementLegacy> => ({
    type: paymentMethod?.type ?? EPaymentType.custom,
    payment_method_id: paymentMethod?.id ?? 'custom_payment_id',
    currency: getPaymentMethodCurrency(paymentMethod?.settings) || 'USD',
    isEditableTotalCost: false,
    isActive: true,
});

/**
 * Signal-form twin of {@link createDefaultPaymentElementForm}. Shared by
 * `web-payment-methods-signal-collection` (which seeds the element when a widget
 * has none) and the payment-form control (which seeds it as part of a product-type
 * switch, so the write travels in the same transaction as the switch).
 */
export const createDefaultPaymentElementLegacy = (
    paymentMethods: PaymentMethod[],
    customPaymentName: string
): PaymentElementLegacy =>
    createPaymentElementLegacy({
        customPaymentName,
        ...getDefaultPaymentElementLegacyData(findActivePaymentMethod(paymentMethods)),
    });
